import { useEffect, useState } from 'react'
import { Button, Card, Chip } from '@heroui/react'
import { api } from '../../lib/api'
import { keyedAddress } from '../../lib/equipment'
import { explain } from '../../lib/staffErrors'
import { formatMoment } from '../../lib/staffText'
import { Icon } from '../../ui/icons'
import { useStaffText } from '../../ui/StaffText'
import '../../styles/branchpage.css'

export interface KeyStatus {
  hasKey: boolean
  issuedAt?: string
  required: boolean
}

/** A machine's key: whether it has one, and a new one whose address is shown once. */
export function ScreenKeySection({ unitId, timezone }: { unitId: string; timezone?: string }) {
  const { lang } = useStaffText()
  const [status, setStatus] = useState<KeyStatus | null>(null)
  const [address, setAddress] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    api
      .screenKeyStatus(unitId)
      .then((s) => alive && setStatus(s))
      .catch((err) => alive && setError(explain(err, lang, 'common.unreachable')))
    return () => {
      alive = false
    }
  }, [unitId, lang])

  const issue = async () => {
    setBusy(true)
    setError('')
    try {
      const r = await api.issueScreenKey(unitId)
      setAddress(keyedAddress(r.url, r.path, window.location.origin))
      setStatus((s) => ({ hasKey: true, issuedAt: r.issuedAt, required: s?.required ?? false }))
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <ScreenKeyView status={status} address={address} busy={busy} error={error} timezone={timezone} onIssue={() => void issue()} />
  )
}

/** What ScreenKeySection draws, apart from its reads. */
export function ScreenKeyView({
  status,
  address,
  busy,
  error,
  timezone,
  onIssue,
}: {
  status: KeyStatus | null
  address: string
  busy: boolean
  error: string
  timezone?: string
  onIssue: () => void
}) {
  const { lang, say } = useStaffText()
  const [copied, setCopied] = useState(false)
  return (
    <Card className="eq-key" role="region" aria-label={say('eq.key.title')}>
      <div className="eq-key__head">
        <strong title={say('eq.key.stopsOld')}>{say('eq.key.title')}</strong>
        {status &&
          (status.hasKey ? (
            <Chip size="sm" variant="secondary">
              {status.issuedAt
                ? say('eq.key.issued', { when: formatMoment(status.issuedAt, lang, { date: true, time: true, zone: timezone }) })
                : say('eq.key.has')}
            </Chip>
          ) : (
            <Chip size="sm" variant="soft" color={status.required ? 'warning' : 'default'}>
              {say(status.required ? 'eq.key.needed' : 'eq.key.none')}
            </Chip>
          ))}
        <Button variant="secondary" size="sm" className="eq-key__new" isDisabled={busy || !status} onPress={onIssue}>
          <Icon name="refresh" size={16} /> {say('eq.key.new')}
        </Button>
      </div>
      {address && (
        <span className="bp-addr eq-key__address">
          <code className="bp-addr__url" title={address}>
            {address}
          </code>
          <Button
            variant="secondary"
            size="sm"
            className="bp-addr__btn"
            onPress={() => {
              void navigator.clipboard?.writeText(address).catch(() => undefined)
              setCopied(true)
              window.setTimeout(() => setCopied(false), 1800)
            }}
            aria-label={say('catalogue.units.copyAddress')}
          >
            <Icon name={copied ? 'check' : 'copy'} size={16} /> {copied ? say('common.copied') : say('catalogue.units.copy')}
          </Button>
        </span>
      )}
      {address && <p className="eq-key__hint">{say('eq.key.once')}</p>}
      {error && <p className="field-error">{error}</p>}
    </Card>
  )
}
